import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@tigerconnect/web-component-library';
import BEM from '../bem';
import propTypes from '../propTypes';
import { ReactComponent as defaultRoleSvg } from '../images/icon-role-default-tag.svg';
import { ReactComponent as whiteRoleSvg } from '../images/icon-role-white.svg';
import { ReactComponent as darkRoleSvg } from '../images/icon-role-dark.svg';
import AvatarImage from './AvatarImage';
import { PresenceIndicator } from './PresenceIndicator';
import { mobxInjectSelect } from 'common/utils';

const userAvatarClasses = BEM.with('UserAvatar');

function RoleAvatar({
  ariaLabel,
  displayName,
  indicatorSize,
  inverse,
  isMuted,
  role,
  size,
  squareAvatar = true,
  useWCL = false,
  subscribeUser,
  unsubscribeUser,
  showPresenceIndicator,
  presenceStatus,
  userId,
  dnd,
  showDndAfStatus,
}) {
  const { tag, metadata } = role;
  const roleHasUser = role?.members?.length > 0;

  useEffect(() => {
    if (userId) {
      subscribeUser(userId);
    }

    return () => {
      if (userId) {
        unsubscribeUser(userId);
      }
    };
  }, [subscribeUser, unsubscribeUser, userId]);

  let roleAvatar, tagColor;
  if (!tag && metadata) {
    roleAvatar = whiteRoleSvg;
    tagColor = metadata.tag_color.replace('0x', '#');
  } else if (!tag || tag.isDefault === true) {
    roleAvatar = defaultRoleSvg;
    tagColor = 'default';
  } else {
    if (tag.color === '#000000' || tag.color === '#003B80') {
      roleAvatar = darkRoleSvg;
    } else {
      roleAvatar = whiteRoleSvg;
    }
    tagColor = tag.color;
  }

  if (useWCL)
    return (
      <Avatar
        type="role"
        size={size}
        bgColor={tagColor !== 'default' ? tagColor : undefined}
        shape="squircle"
      />
    );

  return (
    <AvatarImage
      ariaLabel={ariaLabel}
      avatar={roleAvatar}
      className={userAvatarClasses()}
      displayName={displayName}
      entityType="role"
      inverse={inverse}
      isMuted={isMuted}
      size={size}
      squareAvatar={squareAvatar}
      tagColor={tagColor}
      {...(showPresenceIndicator &&
        roleHasUser && {
          appendChildren: (
            <PresenceIndicator
              presence={presenceStatus}
              size={indicatorSize ? indicatorSize : 'MEDIUM'}
              userId={userId}
              dnd={dnd}
              showDndAfStatus={showDndAfStatus}
            />
          ),
        })}
    />
  );
}

RoleAvatar.propTypes = {
  ariaLabel: PropTypes.string,
  currentOrganizationId: PropTypes.string,
  indicatorSize: PropTypes.string,
  inverse: PropTypes.bool,
  isMuted: PropTypes.bool,
  role: propTypes.role,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  squareAvatar: PropTypes.bool,
  useWCL: PropTypes.bool,
  usePresence: PropTypes.bool,
  userId: PropTypes.string,
  presenceStatus: PropTypes.string,
  showPresenceIndicator: PropTypes.bool,
  dnd: PropTypes.bool,
  showDndAfStatus: PropTypes.bool,
};

export default mobxInjectSelect({
  messengerStore: ['currentOrganizationId'],
  presenceStore: ['subscribeUser', 'unsubscribeUser'],
})(RoleAvatar);
